import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { buildExcerpt, normalizeChatconeName } from './parseAuditReport';

export const SEV_LABEL = { critical: 'วิกฤต', high: 'สูง', med: 'กลาง', low: 'ต่ำ' };
export const SEV_CLASS = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  med: 'bg-yellow-400 text-gray-900',
  low: 'bg-gray-300 text-gray-800'
};

export const STATUS_LABEL = {
  pending: 'รอรับทราบ',
  answered: 'ตอบแล้ว รอหัวหน้าตรวจ',
  discussing: 'กำลังคุยกัน',
  closed: 'ปิดเคสแล้ว',
  withdrawn: 'ถอนเคส'
};
export const STATUS_CLASS = {
  pending: 'bg-red-100 text-red-700 border border-red-300',
  answered: 'bg-blue-100 text-blue-700 border border-blue-300',
  discussing: 'bg-amber-100 text-amber-700 border border-amber-300',
  closed: 'bg-green-100 text-green-700 border border-green-300',
  withdrawn: 'bg-gray-100 text-gray-500 border border-gray-300'
};

/** fix = this person made the mistake (counts toward stats); learn = was in the chat, not at fault */
export const ROLE_LABEL = { fix: 'ต้องปรับ', learn: 'เพื่อเรียนรู้' };
export const ROLE_CLASS = {
  fix: 'bg-red-50 text-red-700 border border-red-200',
  learn: 'bg-green-50 text-green-700 border border-green-200'
};

export const SEND_MODE_LABEL = {
  person: 'รายคน',
  involved: 'ทุกคนในแชท',
  all: 'ส่งทุกคน',
  none: 'ไม่ส่ง · แก้หลังบ้าน'
};

export const PageChip = ({ page }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${page === 'Tama' ? 'bg-pink-100 text-pink-700' : 'bg-sky-100 text-sky-700'}`}>
    {page === 'Tama' ? 'TamaChan' : 'Smile'}
  </span>
);

export const SevChip = ({ sev }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${SEV_CLASS[sev] || 'bg-gray-200'}`}>
    {SEV_LABEL[sev] || sev}
  </span>
);

export const StatusChip = ({ status }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_CLASS[status] || 'bg-gray-100'}`}>
    {STATUS_LABEL[status] || status}
  </span>
);

export const RoleChip = ({ role }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${ROLE_CLASS[role] || 'bg-gray-100'}`}>
    {ROLE_LABEL[role] || role}
  </span>
);

export const fmtSeconds = (s) => {
  if (s == null) return '—';
  if (s < 60) return `${s} วิ`;
  return `${Math.floor(s / 60)} นาที ${s % 60} วิ`;
};

const THAI_DAYS = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'];
/** 'พฤ. 17:00' — the deadline as an admin would say it */
export const fmtDue = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const sameDay = d.toDateString() === new Date().toDateString();
  return `${sameDay ? 'วันนี้' : THAI_DAYS[d.getDay()]} ${hh}:${mm}`;
};
export const isOverdue = (item) => item?.status === 'pending' && item?.dueAt && new Date(item.dueAt).getTime() < Date.now();

export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' }) : '—');

/** Render text with any URLs inside it as clickable links */
export const TextWithLinks = ({ text }) => {
  if (!text) return null;
  const parts = String(text).split(/(https?:\/\/[^\s]+)/g);
  return (
    <>
      {parts.map((part, i) =>
        /^https?:\/\//.test(part) ? (
          <a key={i} href={part} target="_blank" rel="noreferrer" className="text-indigo-600 underline break-all">
            {part}
          </a>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

/**
 * The audit report writes its paragraphs as points joined by " · " (and
 * sometimes "(1) … (2) …"). Split those so a case reads as a list, not a wall.
 */
export const splitPoints = (text) => {
  if (!text) return [];
  const out = [];
  String(text)
    .split(/\s+[·•]\s+|\n+/)
    .forEach((part) => {
      part.split(/\s+(?=\(\d{1,2}\)\s)/).forEach((s) => {
        const t = s.trim();
        if (t) out.push(t);
      });
    });
  return out;
};

/** Report text as bullet lines; `max` shows the first N points with an "…อีก X ข้อ" tail */
export const BulletText = ({ text, max, className = '' }) => {
  const points = splitPoints(text);
  if (points.length <= 1) {
    return <div className={`whitespace-pre-wrap ${className}`}><TextWithLinks text={text} /></div>;
  }
  const shown = max ? points.slice(0, max) : points;
  return (
    <ul className={`space-y-1.5 ${className}`}>
      {shown.map((p, i) => (
        <li key={i} className="flex gap-2 leading-relaxed">
          <span className="text-gray-400 flex-shrink-0 select-none">•</span>
          <span className="min-w-0 break-words"><TextWithLinks text={p} /></span>
        </li>
      ))}
      {max && points.length > max && (
        <li className="text-xs text-gray-400 pl-4">…อีก {points.length - max} ข้อ</li>
      )}
    </ul>
  );
};

/**
 * Fullscreen image viewer with a close button. Rendered through a portal to
 * <body> — inside react-modal the transformed container would otherwise trap
 * position:fixed and the overlay could not cover the screen.
 */
export const ImageLightbox = ({ url, onClose }) => {
  if (!url) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[1400] bg-black bg-opacity-85 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-5 text-white text-4xl leading-none hover:text-gray-300"
        aria-label="ปิด"
      >
        ✕
      </button>
      <img
        src={url}
        alt=""
        className="max-w-[92vw] max-h-[92vh] object-contain rounded"
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body
  );
};

const TH_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
/** "2026-10-06T09:00:12+07:00" → "6 ต.ค." (read off the string, so the browser's timezone can't shift it) */
const thaiDay = (ts) => {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(ts || '');
  return m ? `${Number(m[2])} ${TH_MONTHS[Number(m[1]) - 1]}` : '';
};

/**
 * Comments pinned to chat bubbles, like comments on a Figma frame.
 * A pin lives on the chat entry itself (so the backend stores it inside
 * chatLog untouched):
 *   note   — the comment ("ตรงนี้ลูกค้าถามยี่ห้อ แต่ได้ตัวอย่างฟิกเกอร์")
 *   better — admin bubbles only: what should have been sent instead
 *   pin    — true while a freshly added pin is still empty
 * Pins are numbered in chat order over the whole log, so ① stays ① whether
 * the excerpt or the full day is shown.
 */
export const isPinned = (e) => !!(e && (e.pin || e.note || e.better));

export const pinNumbers = (chatLog) => {
  const map = {};
  let n = 0;
  (Array.isArray(chatLog) ? chatLog : []).forEach((e, i) => { if (isPinned(e)) map[i] = ++n; });
  return map;
};

const PinBadge = ({ n, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title="ดูคอมเมนต์นี้"
    className={`flex-shrink-0 self-start mt-1 w-6 h-6 rounded-full rounded-bl-none text-[11px] font-bold text-white ${active ? 'bg-orange-600 ring-2 ring-orange-300' : 'bg-orange-500 hover:bg-orange-600'}`}
  >
    {n}
  </button>
);

/**
 * Chat transcript bubbles: customer left / admin right / system centered.
 * highlightNames (Chatcone names) get the "คุณ" treatment so an admin finds
 * their own messages at a glance. indexes limits the view to a subset.
 * pins/activePin/onPinClick draw the comment badges; onAddPin turns on the
 * "+ คอมเมนต์" button beside every bubble (import page).
 */
export const ChatLogView = ({
  chatLog, indexes, highlightNames = [], onImageClick, maxHeight = 'max-h-96',
  pins = {}, activePin = null, onPinClick, onAddPin, containerRef
}) => {
  if (!Array.isArray(chatLog) || chatLog.length === 0) return null;
  const mine = new Set(highlightNames.map(normalizeChatconeName));
  const rows = (indexes || chatLog.map((_, i) => i)).map((i) => ({ entry: chatLog[i], i }));
  let dividerShown = false;
  return (
    <div ref={containerRef} className={`space-y-1.5 bg-gray-50 rounded-lg p-3 ${maxHeight} overflow-y-auto`}>
      {rows.map(({ entry, i }, k) => {
        const showDivider = !entry.p && !dividerShown && rows[0].entry.p;
        if (showDivider) dividerShown = true;
        // messages skipped between two shown bubbles (excerpt + a far-off comment)
        const lead = (
          <>
            {showDivider && <div className="text-center text-[11px] text-gray-400 border-t border-dashed border-gray-300 pt-1">— วันที่เกิดเรื่อง —</div>}
            {k > 0 && i !== rows[k - 1].i + 1 && <div className="text-center text-[11px] text-gray-400">··· ข้ามไป {thaiDay(entry.ts)} {entry.t} ···</div>}
          </>
        );
        if (entry.s === 's') {
          const missed = /^missed/i.test(entry.x || '');
          return (
            <React.Fragment key={i}>
              {lead}
              <div className={`text-center text-xs py-0.5 ${missed ? 'text-red-600 font-semibold' : 'text-gray-500 italic'}`}>
                {entry.t} · {missed ? '⚠ ไม่มีใครตอบ (Missed)' : <TextWithLinks text={entry.x} />}
              </div>
            </React.Fragment>
          );
        }
        const isAdmin = entry.s === 'a';
        const isMine = isAdmin && entry.n && mine.has(normalizeChatconeName(entry.n));
        const pinNo = pins[i];
        const isActive = pinNo && activePin === i;
        const badge = pinNo
          ? <PinBadge n={pinNo} active={isActive} onClick={() => onPinClick && onPinClick(i)} />
          : (onAddPin && !entry.p
            ? <button type="button" onClick={() => onAddPin(i)} title="คอมเมนต์ที่ข้อความนี้" className="flex-shrink-0 self-start mt-1 text-[11px] text-gray-400 hover:text-orange-600 whitespace-nowrap">+ คอมเมนต์</button>
            : null);
        return (
          <React.Fragment key={i}>
            {lead}
            <div data-bubble={i} className={`flex items-start gap-1.5 ${isAdmin ? 'justify-end' : 'justify-start'}`}>
              {isAdmin && badge}
              <div className={`max-w-[75%] rounded-lg px-3 py-1.5 text-sm ${isActive ? 'ring-2 ring-orange-400' : ''} ${isMine ? 'bg-indigo-100 border-2 border-indigo-400 text-indigo-900' : isAdmin ? 'bg-blue-50 border border-blue-200 text-blue-900' : 'bg-white border border-gray-200 text-gray-800'}`}>
                <div className="text-[10px] text-gray-500 mb-0.5">
                  {entry.t}{isAdmin && entry.n ? ` · ${entry.n}` : ''}{isMine ? ' (คุณ)' : ''}{!isAdmin ? ' · ลูกค้า' : ''}{entry.p ? ' · เมื่อวาน' : ''}
                </div>
                {entry.x && <div className="whitespace-pre-wrap break-words"><TextWithLinks text={entry.x} /></div>}
                {(entry.i || []).map((url, j) => (
                  <button key={j} type="button" onClick={() => onImageClick && onImageClick(url)} className="block">
                    <img src={url} alt="" className="mt-1 rounded max-h-40 object-contain" />
                  </button>
                ))}
              </div>
              {!isAdmin && badge}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const snippet = (text, len = 70) => {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  return t.length > len ? `${t.slice(0, len)}…` : t || '[รูป]';
};

/** One comment card in the list beside the chat. Editable when onChange is given. */
const PinCard = ({ n, entry, active, onSelect, onChange }) => {
  const isAdmin = entry.s === 'a';
  const who = isAdmin ? (entry.n || 'แอดมิน') : 'ลูกค้า';
  return (
    <div
      onClick={onSelect}
      className={`rounded-lg border p-2.5 text-sm cursor-pointer ${active ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white hover:border-orange-300'}`}
    >
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span className="w-5 h-5 rounded-full rounded-bl-none bg-orange-500 text-white text-[11px] font-bold flex items-center justify-center">{n}</span>
        <span>{entry.t} · {who}</span>
        {onChange && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange({ pin: undefined, note: undefined, better: undefined }); }}
            className="ml-auto text-gray-400 hover:text-red-600"
          >
            ลบ
          </button>
        )}
      </div>
      <div className="mt-1 text-xs text-gray-400 italic">“{snippet(entry.x)}”</div>
      {onChange ? (
        <div className="mt-1.5 space-y-1.5" onClick={(e) => e.stopPropagation()}>
          <textarea
            value={entry.note || ''}
            onChange={(e) => onChange({ note: e.target.value })}
            onFocus={onSelect}
            rows={2}
            autoFocus={!entry.note && !entry.better}
            className="w-full border rounded px-2 py-1 text-sm"
            placeholder="คอมเมนต์ เช่น ลูกค้าถามยี่ห้อ แต่ตอบเป็นข้อความสำเร็จรูป"
          />
          {isAdmin && (
            <>
              <div className="text-[11px] font-semibold text-green-800">✎ ควรตอบแบบนี้</div>
              <textarea
                value={entry.better || ''}
                onChange={(e) => onChange({ better: e.target.value })}
                onFocus={onSelect}
                rows={3}
                className="w-full border border-green-300 bg-green-50 rounded px-2 py-1 text-sm"
                placeholder="ข้อความที่แอดมินควรพิมพ์ตอบตรงนี้ (ไม่ใส่ก็ได้)"
              />
            </>
          )}
        </div>
      ) : (
        <>
          {entry.note && <div className="mt-1.5 text-gray-800 whitespace-pre-wrap break-words"><TextWithLinks text={entry.note} /></div>}
          {entry.better && (
            <div className="mt-1.5 rounded bg-green-50 border border-green-300 px-2 py-1.5">
              <div className="text-[11px] font-semibold text-green-800 mb-0.5">✎ ควรตอบแบบนี้</div>
              <div className="whitespace-pre-wrap break-words text-gray-900"><TextWithLinks text={entry.better} /></div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * The chat with its pinned comments in a list beside it (below it on a phone).
 * Clicking a pin or a card highlights the other and scrolls the chat to the
 * bubble. onEditPin(index, changes) makes the comments editable and lets the
 * supervisor pin a new one on any bubble.
 */
export const ChatWithPins = ({ chatLog, indexes, highlightNames = [], onImageClick, maxHeight, onEditPin }) => {
  const [active, setActive] = useState(null);
  const chatRef = useRef(null);
  // Side-by-side only when the box itself is wide: the admin's drawer is 640px
  // even on a big screen, so the viewport width says nothing here.
  const boxRef = useRef(null);
  const [wide, setWide] = useState(false);
  useEffect(() => {
    if (!boxRef.current) return undefined;
    setWide(boxRef.current.getBoundingClientRect().width >= 760);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setWide(entry.contentRect.width >= 760));
    ro.observe(boxRef.current);
    return () => ro.disconnect();
  }, []);
  if (!Array.isArray(chatLog) || chatLog.length === 0) return null;
  const pins = pinNumbers(chatLog);
  const shown = new Set(indexes || chatLog.map((_, i) => i));
  const pinIdx = Object.keys(pins).map(Number);

  const select = (i) => {
    setActive(i);
    const el = chatRef.current && chatRef.current.querySelector(`[data-bubble="${i}"]`);
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };
  const addPin = (i) => {
    onEditPin(i, { pin: true });
    setActive(i);
  };

  const hasList = pinIdx.length > 0;
  return (
    <div ref={boxRef} className={hasList ? `grid gap-3 ${wide ? 'grid-cols-[minmax(0,1fr)_18rem]' : ''}` : ''}>
      <ChatLogView
        chatLog={chatLog}
        indexes={indexes}
        highlightNames={highlightNames}
        onImageClick={onImageClick}
        maxHeight={maxHeight}
        pins={pins}
        activePin={active}
        onPinClick={select}
        onAddPin={onEditPin ? addPin : null}
        containerRef={chatRef}
      />
      {hasList && (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-600">💬 คอมเมนต์ในแชท {pinIdx.length} จุด</div>
          {pinIdx.map((i) => (
            <div key={i} className={shown.has(i) ? '' : 'opacity-60'}>
              <PinCard
                n={pins[i]}
                entry={chatLog[i]}
                active={active === i}
                onSelect={() => select(i)}
                onChange={onEditPin ? (changes) => onEditPin(i, changes) : null}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * The case as one person should read it: their own part first, then what
 * happened and the standard, then the chat around the incident. Works for the
 * admin (recipient = their row) and for a supervisor looking at that row.
 * endRef marks the bottom for the read tracker.
 */
export const CaseReader = ({ caseData, recipient, highlightNames = [], endRef, viewerName }) => {
  const [showFullDay, setShowFullDay] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  if (!caseData) return null;

  const excerpt = buildExcerpt(caseData.chatLog, caseData.timeRange);
  const isPending = recipient?.status === 'pending';
  const role = recipient?.role;
  const yoursLabel = viewerName ? `ส่วนของ ${viewerName}` : 'ส่วนของคุณ';
  const hasPersonal = !!(recipient && (recipient.personalWhat || recipient.personalNext));
  const mineNorm = new Set(highlightNames.map(normalizeChatconeName));
  const myPins = (Array.isArray(caseData.chatLog) ? caseData.chatLog : [])
    .filter((e) => e.s === 'a' && (e.note || e.better) && e.n && mineNorm.has(normalizeChatconeName(e.n))).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap text-sm text-gray-600">
        <PageChip page={caseData.page} />
        <SevChip sev={caseData.severity} />
        <span className="px-2 py-0.5 rounded text-xs bg-gray-100 border border-gray-300">{caseData.category}</span>
        {role && <RoleChip role={role} />}
        {caseData.reportDate && <span>วันที่ {caseData.reportDate}</span>}
        {caseData.timeRange && <span>ช่วง {caseData.timeRange}</span>}
        {caseData.customerChannel && <span className="text-xs text-gray-400">{caseData.customerChannel}</span>}
        {isPending && recipient?.dueAt && (
          <span className={`ml-auto text-xs font-semibold ${isOverdue(recipient) ? 'text-red-600' : 'text-gray-600'}`}>
            {isOverdue(recipient) ? 'เลยกำหนดแล้ว · ' : ''}ต้องรับทราบก่อน {fmtDue(recipient.dueAt)}
          </span>
        )}
      </div>
      {caseData.customerName && (
        <div className="text-sm text-gray-700 flex items-center gap-2 flex-wrap">
          <span>
            ลูกค้า: <b>{caseData.customerName}</b>
          </span>
          {caseData.chatUrl && (
            <a href={caseData.chatUrl} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 underline">เปิดห้องแชทใน Chatcone ↗</a>
          )}
        </div>
      )}
      {caseData.loadNote && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">⚡ {caseData.loadNote}</div>
      )}

      {recipient && (
        <div className={`rounded-lg p-3 border-2 ${role === 'learn' ? 'bg-green-50 border-green-300' : 'bg-purple-50 border-purple-300'}`}>
          <div className={`text-xs font-bold mb-1 ${role === 'learn' ? 'text-green-800' : 'text-purple-800'}`}>
            {yoursLabel}{role === 'learn' ? ' · อยู่ในแชทนี้ แต่ไม่ใช่คนที่พลาด ส่งให้ดูเพื่อเรียนรู้' : ''}
          </div>
          {hasPersonal ? (
            <>
              {recipient.personalWhat && (
                <BulletText text={recipient.personalWhat} className="text-[15px] text-gray-900" />
              )}
              {recipient.personalNext && (
                <div className="mt-2 pt-2 border-t border-purple-200">
                  <div className="text-xs font-semibold text-purple-700 mb-0.5">ครั้งหน้า</div>
                  <BulletText text={recipient.personalNext} className="text-[15px] text-gray-900" />
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-gray-600">
              {role === 'learn' ? 'อ่านเคสด้านล่าง แล้วลองคิดว่าถ้าเป็นเรา จะตอบลูกค้าว่าอะไร' : 'ดูรายละเอียดในหัวข้อ "เกิดอะไรขึ้น" ด้านล่าง'}
            </div>
          )}
          {myPins > 0 && (
            <div className="mt-2 text-sm font-semibold text-orange-800 bg-orange-50 border border-orange-300 rounded px-2 py-1">
              💬 หัวหน้าคอมเมนต์ที่ข้อความของคุณ {myPins} จุด ดูเลขสีส้มในแชทด้านล่าง กดที่เลขเพื่อดูว่าควรตอบยังไง
            </div>
          )}
        </div>
      )}

      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
        <div className="text-sm font-semibold text-red-700 mb-1.5">เกิดอะไรขึ้น</div>
        <BulletText text={caseData.issue} className="text-sm text-gray-800" />
      </div>

      {caseData.fix && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="text-sm font-semibold text-green-700 mb-1.5">มาตรฐานที่ต้องใช้ต่อไป</div>
          <BulletText text={caseData.fix} className="text-sm text-gray-800" />
        </div>
      )}

      {Array.isArray(caseData.chatLog) && caseData.chatLog.length > 0 && (
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-semibold text-gray-700">
              💬 {showFullDay || !excerpt.focused ? `แชททั้งวัน (${caseData.chatLog.length} ข้อความ)` : `ช่วงที่เกิดเรื่อง (${excerpt.indexes.length} ข้อความ)`}
            </div>
            {excerpt.focused && (
              <button
                type="button"
                onClick={() => setShowFullDay(!showFullDay)}
                className="text-xs text-indigo-600 hover:text-indigo-800"
              >
                {showFullDay ? 'ดูเฉพาะช่วงที่เกิดเรื่อง' : 'ดูแชททั้งวัน'}
              </button>
            )}
          </div>
          <div className="text-xs text-gray-400 mb-1.5">ฝั่งขวาคือแอดมิน ฝั่งซ้ายคือลูกค้า · กดที่รูปเพื่อดูภาพขยาย</div>
          <ChatWithPins
            chatLog={caseData.chatLog}
            indexes={showFullDay ? null : excerpt.indexes}
            highlightNames={highlightNames}
            onImageClick={setLightboxUrl}
            maxHeight={showFullDay ? 'max-h-[28rem]' : 'max-h-none'}
          />
        </div>
      )}

      <div ref={endRef} className="h-1" aria-hidden="true" />
      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </div>
  );
};

export const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 1300
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    width: '760px',
    maxWidth: '95vw',
    maxHeight: '92vh',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: 0,
    borderRadius: '12px'
  }
};
